/**
 * @file item.tsx
 * @description Компонент для отображения пункта бокового меню.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react, react-router-dom, react-redux, classnames, lodash, csstype, react-transition-group, @consta/icons/IconAdd, @consta/icons/IconRemove, @store/slices/menu-slice, @store/index, @src/types, @components/left-menu/item/components, @store/slices/modals-queue-slice]
 * @related-files [@store/slices/menu-slice, @store/index, @src/types, ./components]
 */

import { Link, Delimeter, TitleDelimeter } from './components';
import { useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveGroup, setActiveItem, setSubmenuItems, getSubmenuItems } from '@store/slices/menu-slice';
import { IconAdd } from '@consta/icons/IconAdd';
import { IconRemove } from '@consta/icons/IconRemove';
import { Transition, TransitionStatus } from 'react-transition-group';
import cn from 'classnames';
import _ from 'lodash';
import CSS from 'csstype';
import { AppDispatch } from '@store/index';
import { TMenuItem } from '@src/types';
import { getItems } from '@store/slices/modals-queue-slice';

/**
 * Props для компонента пункта бокового меню
 */
interface IItemProps {
    /** Элемент меню для отображения */
    item: TMenuItem;
    /** Активный элемент меню */
    activeItem: TMenuItem | null;
    /** Активная группа меню */
    activeGroup: TMenuItem | null;
    /** Обработчик изменения активного элемента */
    onChangeActiveItem: (item: TMenuItem) => void;
    /** Заблокирован ли элемент меню */
    disabled: boolean | undefined;
}

export const Item = ({ item, activeItem, activeGroup, onChangeActiveItem, disabled = false }: IItemProps) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch<AppDispatch>();
    const submenuItems: TMenuItem[] = useSelector(getSubmenuItems);
    const isOpen: boolean = useMemo(() => item.id === activeGroup?.id, [activeGroup]);
    const modals: string[] = useSelector(getItems);

    const transitionStyles: Partial<Record<TransitionStatus, CSS.Properties>> = {
        entering: { maxHeight: 0, transition: `max-height ${300}ms ease-out` },
        entered: { maxHeight: '32.508vmin', transition: `max-height ${400}ms ease-in 100ms` },
        exiting: { maxHeight: 0, transition: `max-height ${300}ms ease-out` },
        exited: { maxHeight: 0, transition: `max-height ${300}ms ease-out` }
    };

    const defaultStyle: Partial<CSS.Properties> = {
        overflow: 'hidden',
        maxHeight: 0
    };

    if (item.$type === 'delimiter') {
        return <Delimeter />;
    }

    if (item.$type === 'textDelimiter') {
        return <TitleDelimeter title={item.title} />;
    }

    return (
        <div
            className={cn('', {
                'text-[#97B2C4]': item.id === activeGroup?.id,
                'text-[#a4a5a5]': disabled,
                open: isOpen,
                'cursor-pointer': !disabled
            })}
        >
            <div
                className={cn('gap-[1.0585vmin] px-[1.2703vmin]', {
                    LeftMenuItem_caption: !disabled,
                    'h-[4.9778vmin] flex items-center': disabled
                })}
                onClick={(e) => {
                    if (disabled) {
                        return;
                    }

                    e.preventDefault();

                    if (item.id === activeGroup?.id) {
                        dispatch(setActiveGroup(null));
                        return;
                    }

                    dispatch(setActiveGroup(item));
                }}
            >
                {item.$subMenu?.length > 0 &&
                    (isOpen ? (
                        <IconRemove
                            className="LeftMenuItem_caption_icon min-w-[1.3761vmin] min-h-[1.3761vmin] aspect-square"
                            size="xs"
                        />
                    ) : (
                        <IconAdd
                            className="LeftMenuItem_caption_icon min-w-[1.3761vmin] min-h-[1.3761vmin] aspect-square"
                            size="xs"
                        />
                    ))}

                <a className={cn('LeftMenuItem_caption_title text-inherit', { '!text-[#a4a5a5]': disabled })}>
                    {item.title}
                </a>
            </div>

            <Transition nodeRef={nodeRef} in={isOpen} exit={!isOpen} timeout={0}>
                {(state) => (
                    <div
                        style={{
                            ...defaultStyle,
                            ...transitionStyles[state]
                        }}
                        ref={nodeRef}
                    >
                        {item.$subMenu?.map((subMenuItem: TMenuItem, key: number) => {
                            return (
                                <div
                                    key={key}
                                    className={cn('LeftMenuSubItem flex items-center', {
                                        'text-[#97B2C4]': subMenuItem.id === activeItem?.id,
                                        active: subMenuItem.id === activeItem?.id,
                                        'cursor-default': subMenuItem.$isDisabled
                                    })}
                                    onClick={() => {
                                        if (subMenuItem.$isDisabled) {
                                            return;
                                        }

                                        if (activeItem?.id === subMenuItem.id) {
                                            if (!submenuItems?.length) {
                                                dispatch(setActiveItem(_.cloneDeep(subMenuItem)));
                                                return;
                                            }

                                            dispatch(setActiveItem(null));
                                            dispatch(setSubmenuItems([]));
                                            return;
                                        }

                                        if (!modals?.length || subMenuItem.$type !== 'link') {
                                            onChangeActiveItem(subMenuItem);
                                        }
                                    }}
                                >
                                    {(subMenuItem.$type === 'link' || subMenuItem.$type === 'context') && (
                                        <Link
                                            isActive={subMenuItem.id === activeItem?.id}
                                            isDisabled={subMenuItem.$isDisabled}
                                            target={modals?.length && subMenuItem.$type === 'link' ? '_blank' : '_self'}
                                            isParent={subMenuItem.$type === 'context'}
                                            to={subMenuItem.$type === 'link' ? subMenuItem.$route : undefined}
                                            title={subMenuItem.title}
                                        />
                                    )}

                                    {subMenuItem.$type === 'delimiter' && <Delimeter />}

                                    {subMenuItem.$type === 'textDelimiter' && (
                                        <TitleDelimeter title={subMenuItem.title} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Transition>
        </div>
    );
};
